import { Icon, type IconName } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';

interface EmptyStateProps {
  /** Large decorative icon */
  icon: IconName;
  /** Primary message */
  title: string;
  /** Secondary description */
  description?: string;
  /** Primary action button */
  action?: {
    label: string;
    onClick: () => void;
  };
  /** Secondary action */
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  /** Compact variant for inline use */
  variant?: 'page' | 'inline' | 'card';
}

/**
 * Standardized empty state with variants.
 *
 * Replaces 5+ different inline empty state patterns across the app.
 * Use for: no results due to filters, no entities yet (first run), offline mode.
 *
 * Variants:
 * - page: py-16, full-screen empty state
 * - inline: py-6, compact for panels
 * - card: Centered layout with gap-4 (default)
 *
 * @example
 * ```tsx
 * <EmptyState
 *   icon="corporate_fare"
 *   title="Nessuna azienda"
 *   description="Crea la tua prima azienda per iniziare"
 *   action={{ label: "Nuova azienda", onClick: () => openCreate() }}
 *   variant="card"
 * />
 *
 * <EmptyState
 *   icon="search"
 *   title="No results"
 *   description="Try adjusting your filters"
 *   variant="inline"
 * />
 * ```
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  secondaryAction,
  variant = 'card',
}: EmptyStateProps) {
  const wrapperClass =
    variant === 'inline'
      ? 'py-6 text-center'
      : variant === 'page'
        ? 'py-16 text-center'
        : 'flex flex-col items-center justify-center gap-4 px-6 py-16 text-center';

  return (
    <div className={wrapperClass}>
      <Icon name={icon} className="icon-3xl icon-decorative mb-2" />
      <div className="space-y-2">
        <p className="text-lg font-semibold text-text-primary">{title}</p>
        {description && <p className="text-sm text-text-secondary">{description}</p>}
      </div>
      {(action || secondaryAction) && (
        <div className="flex flex-wrap items-center justify-center gap-3 pt-4">
          {action && <Button onClick={action.onClick}>{action.label}</Button>}
          {secondaryAction && (
            <Button variant="outline" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
